import React from "react";
import ReactDOM from "react-dom/client";
import { Activity, Clock, ExternalLink, KeyRound, Lock, RefreshCcw, Server, ShieldCheck, Wifi, WifiOff } from "lucide-react";
import { fetchState, readStoredToken, refreshServer, storeToken } from "./api";
import type { DashboardState, LinkRecord, ServerRecord } from "./shared/types";
import "./styles.css";

const iconMap = { Activity, Clock, ExternalLink, KeyRound, Lock, RefreshCcw, Server, ShieldCheck, Wifi, WifiOff };

function App() {
  const [token, setToken] = React.useState(readStoredToken);
  const [draftToken, setDraftToken] = React.useState(readStoredToken);
  const [state, setState] = React.useState<DashboardState | null>(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState<Record<string, boolean>>({});
  const [fastPollingUntil, setFastPollingUntil] = React.useState(0);
  const [localCommands, setLocalCommands] = React.useState<Record<string, string>>({});

  const loadState = React.useCallback(async (options: { showLoading?: boolean } = {}) => {
    if (!token) return;
    const showLoading = options.showLoading ?? true;
    if (showLoading) setLoading(true);
    setError("");
    try {
      setState(await fetchState(token));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "상태를 불러오지 못했습니다.");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    void loadState();
    const timer = window.setInterval(() => void loadState({ showLoading: false }), 10000);
    return () => window.clearInterval(timer);
  }, [loadState]);

  React.useEffect(() => {
    if (!token || !fastPollingUntil) return undefined;
    const timer = window.setInterval(() => {
      void loadState({ showLoading: false });
      if (Date.now() > fastPollingUntil) setFastPollingUntil(0);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [fastPollingUntil, loadState, token]);

  React.useEffect(() => {
    if (!state) return;
    setLocalCommands((current) => {
      const next = { ...current };
      for (const server of state.servers) {
        const commandId = next[server.id];
        if (!commandId) continue;
        const command = state.commands.find((item) => item.id === commandId);
        const hasNewRefreshLink = server.links.some((link) => link.kind === "refresh" && Date.parse(link.updatedAt) >= Date.parse(command?.requestedAt || ""));
        if (command?.status === "succeeded" || command?.status === "failed" || hasNewRefreshLink) {
          delete next[server.id];
        }
      }
      return next;
    });
  }, [state]);

  function saveToken(event: React.FormEvent) {
    event.preventDefault();
    storeToken(draftToken.trim());
    setToken(draftToken.trim());
  }

  async function requestRefresh(serverId: string) {
    setRefreshing((current) => ({ ...current, [serverId]: true }));
    setError("");
    try {
      const response = await refreshServer(token, serverId);
      if (response.command?.id) {
        setLocalCommands((current) => ({ ...current, [serverId]: response.command.id }));
      }
      setFastPollingUntil(Date.now() + 120000);
      await loadState({ showLoading: false });
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : "새 링크 발급 명령을 만들지 못했습니다.");
    } finally {
      setRefreshing((current) => ({ ...current, [serverId]: false }));
    }
  }

  const summary = summarize(state);
  return (
    <main className="app-shell">
      <section className="topbar">
        <div>
          <p className="eyebrow">Cloudflare Quick Tunnel Control</p>
          <h1>임시 터널 관제 대시보드</h1>
        </div>
        <form className="token-form" onSubmit={saveToken}>
          <iconMap.Lock aria-hidden="true" size={17} />
          <input
            aria-label="관리자 토큰"
            type="password"
            value={draftToken}
            onChange={(event) => setDraftToken(event.target.value)}
            placeholder="관리자 토큰"
          />
          <button type="submit">연결</button>
        </form>
      </section>

      <section className="summary-grid" aria-label="요약">
        <SummaryTile icon={iconMap.Server} label="관리 서버" value={`${summary.total}`} />
        <SummaryTile icon={iconMap.Wifi} label="접속 가능" value={`${summary.online}`} />
        <SummaryTile icon={iconMap.WifiOff} label="접속 실패" value={`${summary.offline}`} />
        <SummaryTile icon={iconMap.Clock} label="마지막 갱신" value={formatTime(state?.generatedAt)} />
      </section>

      {error && <div className="alert">{error}</div>}
      {!token && <div className="empty-state">Cloudflare Worker에 설정한 관리자 토큰을 입력하면 상태를 볼 수 있습니다.</div>}

      <section className="server-grid" aria-busy={loading}>
        {(state?.servers || []).map((server) => (
          <ServerPanel
            key={server.id}
            server={server}
            loading={Boolean(refreshing[server.id])}
            localCommandId={localCommands[server.id]}
            onRefresh={() => requestRefresh(server.id)}
          />
        ))}
      </section>
    </main>
  );
}

function SummaryTile({ icon: Icon, label, value }: { icon: typeof Activity; label: string; value: string }) {
  return (
    <div className="summary-tile">
      <Icon aria-hidden="true" size={18} />
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ServerPanel({ server, loading, localCommandId, onRefresh }: { server: ServerRecord; loading: boolean; localCommandId?: string; onRefresh: () => void }) {
  const primaryLink = pickPrimaryLink(server);
  const command = server.activeCommand;
  const pendingCommandId = command?.id || localCommandId;
  const newestRefreshLink = pickNewestRefreshLink(server);
  return (
    <article className="server-card">
      <header className="server-header">
        <div>
          <div className="server-title-row">
            <h2>{server.name}</h2>
            <span className="port">:{server.port}</span>
          </div>
          <p>{server.localUrl}</p>
        </div>
        <StatusBadge status={primaryLink?.status || "unknown"} />
      </header>

      <div className="link-block">
        <span className="field-label">현재 링크</span>
        {primaryLink?.url ? (
          <a className="public-link" href={primaryLink.url} target="_blank" rel="noreferrer">
            <span>{primaryLink.url}</span>
            <iconMap.ExternalLink aria-hidden="true" size={16} />
          </a>
        ) : (
          <span className="muted">감지된 링크 없음</span>
        )}
      </div>

      <dl className="meta-grid">
        <div>
          <dt>PID</dt>
          <dd>{primaryLink?.pid || "-"}</dd>
        </div>
        <div>
          <dt>마지막 확인</dt>
          <dd>{formatTime(primaryLink?.lastCheckedAt)}</dd>
        </div>
        <div className="wide">
          <dt>로그</dt>
          <dd title={primaryLink?.logPath || ""}>{primaryLink?.logPath || "-"}</dd>
        </div>
      </dl>

      {primaryLink?.authUser && primaryLink.id !== newestRefreshLink?.id && (
        <div className="credential-box">
          <div>
            <span className="field-label">새 ID</span>
            <strong>{primaryLink.authUser}</strong>
          </div>
          <div>
            <span className="field-label">새 비밀번호</span>
            <strong>{primaryLink.authPassword || "암호화 저장됨"}</strong>
          </div>
        </div>
      )}

      {pendingCommandId && !newestRefreshLink?.authUser && (
        <div className="pending-box">
          <iconMap.RefreshCcw aria-hidden="true" size={16} />
          <span>새 임시 링크를 발급 중입니다. 완료되면 이 카드에 링크와 ID/PW가 자동 표시됩니다.</span>
        </div>
      )}

      {newestRefreshLink?.authUser && (
        <div className="issued-box">
          <div className="issued-title">
            <iconMap.KeyRound aria-hidden="true" size={16} />
            <strong>최근 발급된 접속 정보</strong>
          </div>
          <a className="public-link" href={newestRefreshLink.url} target="_blank" rel="noreferrer">
            <span>{newestRefreshLink.url}</span>
            <iconMap.ExternalLink aria-hidden="true" size={16} />
          </a>
          <div className="credential-box">
            <div>
              <span className="field-label">ID</span>
              <strong>{newestRefreshLink.authUser}</strong>
            </div>
            <div>
              <span className="field-label">비밀번호</span>
              <strong>{newestRefreshLink.authPassword || "암호화 저장됨"}</strong>
            </div>
          </div>
        </div>
      )}

      <footer className="card-actions">
        <span className="command-state">{command ? commandLabel(command.status) : localCommandId ? "명령 전송됨" : "대기 중"}</span>
        <button type="button" onClick={onRefresh} disabled={loading || Boolean(command)}>
          <iconMap.RefreshCcw aria-hidden="true" size={16} />
          {loading ? "요청 중" : "새 링크"}
        </button>
      </footer>
    </article>
  );
}

function StatusBadge({ status }: { status: LinkRecord["status"] }) {
  const label = status === "online" ? "접속 가능" : status === "offline" ? "접속 실패" : status === "checking" ? "확인 중" : "미확인";
  const Icon = status === "online" ? iconMap.Wifi : status === "offline" ? iconMap.WifiOff : iconMap.Activity;
  return (
    <span className={`status status-${status}`}>
      <Icon aria-hidden="true" size={15} />
      {label}
    </span>
  );
}

function pickPrimaryLink(server: ServerRecord) {
  return [...server.links].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "refresh" ? -1 : 1;
    return Date.parse(b.updatedAt) - Date.parse(a.updatedAt);
  })[0];
}

function pickNewestRefreshLink(server: ServerRecord) {
  return server.links
    .filter((link) => link.kind === "refresh")
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))[0];
}

function summarize(state: DashboardState | null) {
  const servers = state?.servers || [];
  let online = 0;
  let offline = 0;
  for (const server of servers) {
    const status = pickPrimaryLink(server)?.status;
    if (status === "online") online += 1;
    if (status === "offline") offline += 1;
  }
  return { total: servers.length, online, offline };
}

function formatTime(value?: string) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }).format(new Date(value));
}

function commandLabel(status: string) {
  return {
    pending: "명령 대기",
    claimed: "에이전트 처리 중",
    running: "새 터널 발급 중",
    succeeded: "완료",
    failed: "실패",
    expired: "만료"
  }[status] || status;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
